import {
  ActionSheetButton,
  IonActionSheet,
  useIonActionSheet,
} from '@ionic/react';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface WithConfirmationProps {
  header?: string;
  destructivePrompt?: string;
  children: (props: ChildProps) => ReactElement;
  disabled?: boolean;
}

interface OnConfirmedParams {
  onConfirmation?: () => void;
  onCancel?: () => void;
}
type OnConfirmed = (params: OnConfirmedParams) => void;

interface ChildProps {
  onConfirmed: OnConfirmed;
  waitingForConfirmation: boolean;
}

const WithConfirmation = (props: WithConfirmationProps): ReactElement => {
  const { children, header, destructivePrompt } = props;
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isConfirmed = useRef<() => void | undefined>();
  const isCanceled = useRef<() => void | undefined>();

  const buttons: ActionSheetButton[] = [
    {
      text: destructivePrompt ?? i18n.t('common:delete_confirmation'),
      role: 'destructive',
      handler: () => {
        if (isConfirmed.current) {
          isConfirmed.current();
        }
      },
    },
    {
      text: i18n.t('common:cancel'),
      role: 'cancel',
      handler: () => {
        if (isCanceled.current) {
          isCanceled.current();
        }
      },
    },
  ];

  const onConfirmed: OnConfirmed = ({ onConfirmation, onCancel }) => {
    setIsOpen(true);
    isConfirmed.current = onConfirmation;
    isCanceled.current = onCancel;
  };

  return (
    <>
      {children({ onConfirmed, waitingForConfirmation: isOpen })}
      <IonActionSheet
        isOpen={isOpen}
        header={header ?? i18n.t('common:delete_confirmation_prompt')}
        onWillDismiss={() => {
          if (isOpen) {
            setIsOpen(false);
          }
        }}
        buttons={buttons}
      />
    </>
  );
};

type WithConfirmationHook = (
  params?: Pick<WithConfirmationProps, 'header' | 'destructivePrompt'>
) => OnConfirmed;

const useWithConfirmation: WithConfirmationHook = (params) => {
  const { header, destructivePrompt } = params ?? {};
  const [present] = useIonActionSheet();
  const { i18n } = useTranslation();

  const withConfirmation = useCallback(
    (params: OnConfirmedParams) => {
      const { onConfirmation, onCancel } = params;
      present({
        header: header ?? i18n.t('common:delete_confirmation_prompt'),
        buttons: [
          {
            text: destructivePrompt ?? i18n.t('common:delete_confirmation'),
            role: 'destructive',
            handler: () => {
              if (onConfirmation) {
                onConfirmation();
              }
            },
          },
          {
            text: i18n.t('common:cancel'),
            role: 'cancel',
            handler: () => {
              if (onCancel) {
                onCancel();
              }
            },
          },
        ],
      });
    },
    [destructivePrompt, header, i18n, present]
  );

  return withConfirmation;
};

export { WithConfirmation, useWithConfirmation };
